import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Loader from "components/ui/Loader";
import { FileImage, MessageCircle, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useToast } from "../../contexts/ToastContext";
import { useGetProfileQuery } from "../../store/api/authApi";
import { useGetConversationsQuery } from "../../store/api/conversationsApi";
import {
	useCreatePropertyMutation,
	useListPropertiesQuery,
	useUpdatePropertyMutation,
	type PropertyData,
} from "../../store/api/propertiesApi";
import { useListViewingsQuery } from "../../store/api/propertyViewingsApi";

import ListPropertyModal from "./components/ListPropertyModal";
import LiveChatInterface from "./components/LiveChatInterface";
import PropertyImageGallery from "./components/PropertyImageGallery";
import SalesFilters from "./components/SalesFilters";
import SalesStats from "./components/SalesStats";
import SalesTable from "./components/SalesTable";

interface SalesProperty {
	id: string;
	title: string;
	address: string;
	type: string;
	price: number;
	status: string;
	bedrooms: number;
	bathrooms: number;
	square_feet: number;
	squareFootage?: number; // Alias for square_feet for form compatibility
	images: string[];
	description: string;
	created_at: string;
	viewings: number;
	inquiries: number;
	propertyType?: string; // For form compatibility
}

const SalesManagement = () => {
	const { success, error: showError } = useToast();
	// State management
	const [properties, setProperties] = useState<SalesProperty[]>([]);
	const [filteredProperties, setFilteredProperties] = useState<SalesProperty[]>(
		[],
	);
	const [filters, setFilters] = useState({
		status: "all",
		priceRange: "all",
		propertyType: "all",
		search: "",
	});
	const [selectedProperty, setSelectedProperty] =
		useState<SalesProperty | null>(null);
	const [showListPropertyModal, setShowListPropertyModal] = useState(false);
	const [showImageGallery, setShowImageGallery] = useState(false);
	const [showLiveChat, setShowLiveChat] = useState(false);
	const [activeChatProperty, setActiveChatProperty] =
		useState<SalesProperty | null>(null);

	// Get user profile for organization and role
	const { data: userProfile } = useGetProfileQuery();
	const userRole = userProfile?.role || "";
	const canEdit = userRole === "admin"; // Only admins can edit

	// Fetch properties from API
	const {
		data: propertiesData,
		isLoading: isLoadingProperties,
		refetch: refetchProperties,
	} = useListPropertiesQuery({
		page_size: 1000,
	});

	// Fetch all viewings to count per property
	const { data: viewingsData } = useListViewingsQuery({
		page_size: 1000,
	});

	// Fetch all conversations to count inquiries per property
	const { data: conversationsList } = useGetConversationsQuery();

	// Fetch full conversation details for conversations that might be property-related
	// We'll use a more efficient approach: count conversations that mention properties in subject
	// For now, we'll fetch a few conversation details to check for related_property
	const conversationIds = useMemo(() => {
		return conversationsList?.slice(0, 50).map((conv) => conv.id) || [];
	}, [conversationsList]);

	// Mutations
	const [createProperty, { isLoading: isCreating }] =
		useCreatePropertyMutation();
	const [updateProperty, { isLoading: isUpdating }] =
		useUpdatePropertyMutation();

	// Transform API properties to sales format with viewings and inquiries counts
	const apiProperties = useMemo<SalesProperty[]>(() => {
		if (!propertiesData?.results) return [];

		// Count viewings per property
		const viewingsByProperty: Record<string, number> = {};
		if (viewingsData?.results) {
			viewingsData.results.forEach((viewing) => {
				const propId = viewing.property;
				if (propId) {
					viewingsByProperty[propId] = (viewingsByProperty[propId] || 0) + 1;
				}
			});
		}

		// Count inquiries (conversations) per property
		// Note: ConversationListItem doesn't include related_property, so we'll use a simpler approach
		// For now, we'll count conversations that might be related based on subject matching
		// In a production system, you'd want to fetch full conversation details or modify the backend
		// to include related_property in the list serializer
		const inquiriesByProperty: Record<string, number> = {};
		// TODO: Implement proper property-conversation mapping when backend supports it in list view
		// For now, inquiries count will be 0 until we can efficiently fetch related_property

		return propertiesData.results
			.filter(
				(p) =>
					p.status === "available" ||
					p.status === "leased" ||
					p.status === "sold",
			)
			.map(
				(property): SalesProperty => ({
					id: property.id,
					title: property.name,
					address: property.address,
					type: property.property_type,
					price:
						Number(property.purchase_price) ||
						Number(property.monthly_rent) ||
						0,
					status: property.status === "available" ? "active" : property.status,
					bedrooms: Number(property.bedrooms) || 0,
					bathrooms: Number(property.bathrooms) || 0,
					square_feet: Number(property.square_feet) || 0,
					images: property.images || [],
					description: property.description || "",
					created_at: property.created_at,
					viewings: viewingsByProperty[property.id] || 0,
					inquiries: inquiriesByProperty[property.id] || 0,
				}),
			);
	}, [propertiesData, viewingsData, conversationsList]);

	// Update properties when API data changes
	useEffect(() => {
		setProperties(apiProperties);
	}, [apiProperties]);

	// Filter properties based on current filters
	const filteredPropertiesMemo = useMemo(() => {
		let filtered: SalesProperty[] = properties;

		if (filters.status !== "all") {
			filtered = filtered.filter(
				(property) => property.status === filters.status,
			);
		}

		if (filters.priceRange !== "all") {
			const [min, max] = filters.priceRange
				.split("-")
				.map((val) => Number(val) || 0);
			filtered = filtered.filter((property) => {
				const price = Number(property.price) || 0;
				if (max && !isNaN(max)) {
					return price >= min && price <= max;
				}
				return price >= min;
			});
		}

		if (filters.propertyType !== "all") {
			filtered = filtered.filter(
				(property) => property.type === filters.propertyType,
			);
		}

		if (filters.search) {
				filtered = filtered.filter(
					(property) =>
						property.address
							.toLowerCase()
							.includes(filters.search.toLowerCase()) ||
						property.title.toLowerCase().includes(filters.search.toLowerCase()),
				);
		}

		return filtered;
	}, [properties, filters]);

	// Update filtered properties state
	useEffect(() => {
		setFilteredProperties(filteredPropertiesMemo);
	}, [filteredPropertiesMemo]);

	const handleFilterChange = (newFilters: Partial<typeof filters>) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
	};

	const handleListProperty = async (propertyData: Partial<SalesProperty>) => {

		try {
			// Prepare property data for API
			// Map property type from sales format to backend format
			const mapPropertyType = (type: string): PropertyData["property_type"] => {
				const typeMap: Record<string, PropertyData["property_type"]> = {
					house: "house",
					apartment: "apartment",
					condo: "condo",
					townhouse: "house", // Map townhouse to house
					commercial: "commercial",
					office: "office",
				};
				return typeMap[type] || "house";
			};

			const propertyPayload: PropertyData = {
				// Organization is optional for in-house app
				name: propertyData.title || "",
				address: propertyData.address || "",
				property_type: mapPropertyType(propertyData.type || "house"),
				status: selectedProperty ? undefined : "available", // Don't change status on update unless specified
				bedrooms: propertyData.bedrooms || undefined,
				bathrooms: propertyData.bathrooms || undefined,
				square_feet:
					propertyData.square_feet || propertyData.squareFootage || undefined,
				description: propertyData.description || "",
				images:
					propertyData.images?.filter((img) => !img.startsWith("data:")) || [],
				image_files:
					propertyData.images?.filter((img) => img.startsWith("data:")) || [],
			};

			// Set price based on property type (purchase_price for sales, monthly_rent for rentals)
			if (propertyData.price) {
				// For sales management, we'll use purchase_price
				propertyPayload.purchase_price = propertyData.price;
			}

			if (selectedProperty) {
				// Update existing property
				await updateProperty({
					id: selectedProperty.id,
					data: propertyPayload,
				}).unwrap();
				success("Property updated successfully!");
			} else {
				// Create new property
				await createProperty(propertyPayload).unwrap();
				success("Property listed successfully!");
			}

			// Refresh properties list
			refetchProperties();
			setShowListPropertyModal(false);
			setSelectedProperty(null);
		} catch (error: any) {
			console.error("Error saving property:", error);
			showError(
				error?.data?.error ||
					error?.data?.message ||
					"Failed to save property. Please try again.",
			);
		}
	};

	const handlePropertyAction = (property: SalesProperty, action: string) => {
		switch (action) {
			case "view":
				setSelectedProperty(property);
				setShowImageGallery(true);
				break;
			case "edit":
				setSelectedProperty(property);
				setShowListPropertyModal(true);
				break;
			case "chat":
				setActiveChatProperty(property);
				setShowLiveChat(true);
				break;
			case "schedule":
				// Navigate to viewing management
				window.location.href = `/property-viewing-management?property=${property.id}`;
				break;
			default:
				break;
		}
	};

	const handleStatusUpdate = async (propertyId: string, newStatus: string) => {
		try {
			// Map sales status to backend status
			const backendStatus = newStatus === "active" ? "available" : newStatus;

			await updateProperty({
				id: propertyId,
				data: {
					status: backendStatus as PropertyData["status"],
				},
			}).unwrap();

			success("Property status updated successfully!");
			// Refresh properties list
			refetchProperties();
		} catch (error: any) {
			console.error("Error updating property status:", error);
			showError(
				error?.data?.error ||
					error?.data?.message ||
					"Failed to update property status. Please try again.",
			);
		}
	};

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Header Actions */}
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div>
						<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
							Sales Management
						</h1>
						<p className="text-muted-foreground">
							Manage property listings, buyer inquiries, and sales pipeline
						</p>
					</div>

					<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:flex-shrink-0">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowLiveChat(true)}
							className="flex items-center gap-2 w-full sm:w-auto"
						>
							<MessageCircle size={16} />
							Live Chat
						</Button>
						{canEdit && (
							<Button
								onClick={() => setShowListPropertyModal(true)}
								size="sm"
								className="flex items-center gap-2 w-full sm:w-auto"
							>
								<Plus size={16} />
								List Property
							</Button>
						)}
					</div>
				</div>

				{/* Sales Statistics */}
				<SalesStats properties={properties} />

				{/* Filters */}
				<SalesFilters filters={filters} onFilterChange={handleFilterChange} />

				{/* Properties Table */}
				<div className="bg-card rounded-lg border border-border">
					<div className="p-6">
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-lg font-semibold text-foreground">
								Listed Properties
							</h2>
							<div className="flex items-center gap-3">
								<div className="relative">
									<Search
										className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
										size={16}
									/>
									<Input
										placeholder="Search properties..."
										value={filters?.search}
										onChange={(e) =>
											handleFilterChange({ search: e?.target?.value })
										}
										className="pl-10 w-64"
									/>
								</div>
							</div>
						</div>

						{isLoadingProperties ? (
							<Loader size="lg" showText text="Loading properties..." className="py-8" />
						) : (
							<SalesTable
								properties={filteredProperties}
								onPropertyAction={handlePropertyAction}
								onStatusUpdate={handleStatusUpdate}
								canEdit={canEdit}
							/>
						)}
					</div>
				</div>

				{/* Empty State */}
				{filteredProperties?.length === 0 && (
					<div className="text-center py-12">
						<div className="w-24 h-24 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
							<FileImage className="w-8 h-8 text-muted-foreground" />
						</div>
						<h3 className="text-lg font-semibold text-foreground mb-2">
							No Properties Listed
						</h3>
						<p className="text-muted-foreground mb-6 max-w-md mx-auto">
							Start by listing your first property for sale. Upload images and
							property details to attract potential buyers.
						</p>
						{canEdit && (
							<Button
								onClick={() => setShowListPropertyModal(true)}
								className="flex items-center gap-2 mx-auto"
							>
								<Plus size={16} />
								List Your First Property
							</Button>
						)}
					</div>
				)}
			</div>
			{/* Modals */}
			{showListPropertyModal && (
				<ListPropertyModal
					property={selectedProperty}
					onClose={() => {
						setShowListPropertyModal(false);
						setSelectedProperty(null);
					}}
					onSave={handleListProperty}
					isLoading={isCreating || isUpdating}
				/>
			)}
			{showImageGallery && selectedProperty && (
				<PropertyImageGallery
					property={selectedProperty}
					onClose={() => {
						setShowImageGallery(false);
						setSelectedProperty(null);
					}}
				/>
			)}
			{showLiveChat && (
				<LiveChatInterface
					property={activeChatProperty}
					onClose={() => {
						setShowLiveChat(false);
						setActiveChatProperty(null);
					}}
				/>
			)}
		</div>
	);
};

export default SalesManagement;
